import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiCheckCircle } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { CourseAboutDescription, CourseAboutTitle, CourseBanner, CourseBannerCTA, CourseBannerDescription, CourseBannerTitle } from '@/components/custom/course';
import { Button } from '@/components/ui/button';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Badge } from '@/components/ui/badge';
import { CardKpi, CardKpiGrid } from '@/components/ui/card';
import { Widget, WidgetBody, WidgetHeader, WidgetSubtitle, WidgetText, WidgetTitle } from '@/components/custom/widget';

export function MockExamsAbout({ uri }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const client = useClient();
  const { id } = useParams();
  const [exam, setExam] = useState([]);
  const [courses, setCourses] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [notAllowed, setNotAllowed] = useState(false);
  const [loading, setLoading] = useState(true);

  const getCourses = async () => {
    try {
      const { data } = await client.fetch('courses');
      setCourses(data);
    } catch {
      toast.error(t('toast.errorGetCourse'));
    }
  };

  const getExam = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`question_books/${id}`);
    if (error) {
      toast.error(t('toast.errorGetExam'));
      setNotAllowed(true);
    } else {
      setExam(data);
      setTaxonomies(data.taxonomies);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCourses();
    getExam();
  }, []);

  const courseName = courses?.filter(x => x.id === exam?.['course-ids']?.[0]);

  function formatDateTime(dateTimeString) {
    if (!dateTimeString) return '';

    const dateTime = new Date(dateTimeString);
    const hours = dateTime.getHours().toString().padStart(2, '0');
    const minutes = dateTime.getMinutes().toString().padStart(2, '0');
    return `${hours}h${minutes}`;
  }

  const hours = exam?.['resolution-time'] ? parseInt(exam['resolution-time'].split(':')[0], 10) : 0;

  const warningStart = () => {
    Swal.fire({
      title: t('warning.warningStartExams', { resolutionTime: exam['resolution-time'] }),
      text: t('warning.warningStartExams2'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        navigate(`/simulados/${id}`);
      }
    });
  };

  const breadcrumbs = {
    title: 'Avaliações e Questões',
    nav: [
      {
        route: '/simulados',
        name: 'Simulados',
        isActive: false
      },
      {
        route: uri,
        name: 'Sobre',
        isActive: true
      }
    ]
  };

  return !notAllowed && !loading ? (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <CourseBanner className="mb-12">
        <CourseBannerTitle>{exam?.title}</CourseBannerTitle>
        <CourseBannerDescription asChild>
          <div>
            <p className="mb-4">{t('exams.examBannerDescription')}</p>
            <p className="mb-4">{t('exams.examBannerDescriptionWaterAndBathroom')}</p>
          </div>
        </CourseBannerDescription>

        <CourseBannerCTA>
          {moment().isBetween(exam['published-at'], exam['finished-at'], undefined, true) && (
            <Button
              wide
              questionBookName={exam?.title}
              onClick={warningStart}
            >
              Iniciar Simulado
            </Button>
          )}
        </CourseBannerCTA>
      </CourseBanner>

      <Page>
        <PageContent bg>
          <div>
            <Badge
              size="xs"
              square
              className="bg-[#D6FDFF] text-neutral-dark"
            >
              {courseName[0]?.title}
            </Badge>
            <CourseAboutTitle>{t('exams.titleDescription')}</CourseAboutTitle>
            <CourseAboutDescription>{exam?.description}</CourseAboutDescription>

            {exam?.['published-at'] && exam?.['finished-at'] && (
              <CardKpi className="mb-3">
                <FiCalendar />{' '}
                <span className="font-light">
                  {t('exams.from')}
                  <strong> {moment(exam?.['published-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{exam['published-at'] && formatDateTime(exam['published-at'])}</strong> {t('exams.to')}
                  <strong> {moment(exam?.['finished-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{exam?.['finished-at'] && formatDateTime(exam?.['finished-at'])}</strong>
                </span>
              </CardKpi>
            )}
            <CardKpiGrid>
              <CardKpi>
                <FiClock />
                {hours > 1 ? (
                  <span
                    className="font-light"
                    dangerouslySetInnerHTML={{ __html: t('exams.hoursDuration', { hours: hours }) }}
                  />
                ) : (
                  <span
                    className="font-light"
                    dangerouslySetInnerHTML={{ __html: t('exams.hourDuration', { hours: hours }) }}
                  />
                )}
              </CardKpi>
              <CardKpi>
                <FiCheckCircle />
                <span className="font-light">
                  <strong>{exam?.['question-count']}</strong> {t('exams.questions')}
                </span>
              </CardKpi>
            </CardKpiGrid>
          </div>
        </PageContent>

        <PageSidebar>
          <Widget>
            <WidgetHeader>
              <WidgetTitle>{t('exams.titleMaterialsDistribuition')}</WidgetTitle>
              <WidgetSubtitle>
                {exam?.['question-count']} {t('exams.questions')}
              </WidgetSubtitle>
            </WidgetHeader>

            <WidgetBody>
              {taxonomies.map(taxonomy => (
                <div
                  className="flex items-center justify-between gap-4 border-b border-solid border-bright py-3 last:border-0"
                  key={taxonomy.id}
                  title={taxonomy.name}
                >
                  {taxonomy.name.length > 25 ? (
                    <Badge
                      variant="primary-light"
                      size="xs"
                    >
                      {taxonomy.name.slice(0, 25)}...
                    </Badge>
                  ) : (
                    <Badge
                      variant="primary-light"
                      size="xs"
                    >
                      {taxonomy.name}
                    </Badge>
                  )}

                  <WidgetText>
                    {taxonomy.questionCount} {t('exams.questions')}
                  </WidgetText>
                </div>
              ))}
            </WidgetBody>
          </Widget>
        </PageSidebar>
      </Page>
    </DashboardContent>
  ) : (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Loader />
    </DashboardContent>
  );
}
